import React, { useCallback, useEffect, useMemo } from 'react';

import { styled } from '@compiled/react';
import type { ProjectType } from '@atlassian/jira-common-constants';
import type { Project } from '@atlassian/jira-common-constants/src/project-types';
import { fg } from '@atlassian/jira-feature-gating';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT, type ImportType, CLICK_UP, CSV } from '../common/constants/import-type';
import {
	SELECT_PRODUCT_STEP,
	UPLOAD_CSV_STEP,
	MAPPING_STEP,
	PROJECT_CREATE_STEP,
	IMPORT_IN_PROGRESS_STEP,
	SELECT_THIRD_PARTY_ENTITY_STEP,
	USER_MAPPING_STEP,
	THIRD_PARTY_REVIEW_STEP,
	UPLOAD_CSV_STANDARD_STEP,
	SELECT_SOURCE_STEP,
	CSV_USER_MAPPING_STEP,
	REVIEW_SCREEN_STANDARD_STEP,
} from '../common/constants/steps';
import type { ImporterFlow, OnDrawerClose } from '../common/types';
import {
	AnalyticsAttributesContext,
	useUserAccess,
} from '../common/utils/analtics-attributes-context';
import { getFirstStep } from '../common/utils/import-steps';
import { useImporterContextFromQueryParams } from '../common/utils/importer-context';
import {
	useStep,
	useImportType,
	ImportStateContainer,
	useProjectData,
	useThirdPartyState,
} from '../controllers/import-state';
import { useSource } from '../controllers/import-state/selectors';
import { useProductSteps, usePreventCancelImport } from '../controllers/utils';
import CancelImportModal from './cancel-import-modal';
import { CSVUserMappingScreen } from './csv-user-mapping-screen';
import { ImportInProgressScreen } from './import-in-progress-screen';
import { MappingScreen } from './mapping-screen';
import { ProjectCreateScreen } from './project-create-screen';
import RequestAccessTokenModal from './request-access-token-modal';
import { ReviewScreenStandard } from './review-screen-standard';
import { SelectProductScreen } from './select-product-screen';
import { SelectSourceScreen } from './select-source-screen';
import { SelectThirdPartyEntityScreen } from './select-third-party-entity-screen';
import { ThirdPartyReviewScreen } from './third-party-review-screen';
import { UploadScreen } from './upload-screen';
import { UploadScreenStandard } from './upload-screen-standard';
import { UserMappingScreen } from './user-mapping-screen';

export type Props = {
	onEnterFullScreenMode: () => void;
	onExitFullScreenMode: () => void;
	projectType: ProjectType | null;
	project?: Project | null;
	onClose?: () => void;
	onDrawerClose?: OnDrawerClose | null;
};

const ProjectCreateImporterUploadFlow = ({
	onEnterFullScreenMode,
	onExitFullScreenMode,
	onClose,
	onDrawerClose,
}: Props) => {
	const [, { setStep, setImportTypeAndStep, resetStateAndReturnToUploadStep, resetState }] =
		useStep();
	const [{ type: projectType, style: projectStyle }, { resetProjectData }] = useProjectData();
	const { productSteps, currentStepIndex, currentStepConfig } = useProductSteps();
	usePreventCancelImport();
	const [importType] = useImportType();
	const [, { setThirdPartyImportSettings }] = useThirdPartyState();
	const [source] = useSource();
	const userAccess = useUserAccess();

	useEffect(
		() => () => {
			setImportTypeAndStep({
				importType: DEFAULT,
				step: fg('enable-x2c-standardised-flow') ? SELECT_SOURCE_STEP : SELECT_PRODUCT_STEP,
			});
		},
		[setImportTypeAndStep],
	);

	// used by left hand corner close and return button
	const onCloseAndReturnToUploadStep = useCallback(() => {
		onExitFullScreenMode();
		if (fg('enable-x2c-standardised-flow')) {
			resetState();
		} else {
			resetStateAndReturnToUploadStep();
		}
		onClose && onClose();
	}, [onClose, onExitFullScreenMode, resetStateAndReturnToUploadStep, resetState]);

	const onReturn = useCallback(() => {
		const prevStep =
			currentStepConfig?.overrides?.overrideReturnStep || productSteps[currentStepIndex - 1].step;

		const shouldExitFullScreen = fg('enable-x2c-standardised-flow')
			? prevStep === SELECT_SOURCE_STEP
			: prevStep === UPLOAD_CSV_STEP || prevStep === UPLOAD_CSV_STANDARD_STEP;

		if (shouldExitFullScreen) {
			onExitFullScreenMode();
			// do not reset projectData when navigating back in the csv flow
			if (importType !== CSV) {
				resetProjectData();
			}
			setImportTypeAndStep({ importType: DEFAULT, step: prevStep });
			return;
		}
		setStep(prevStep);
	}, [
		currentStepConfig,
		productSteps,
		currentStepIndex,
		setStep,
		onExitFullScreenMode,
		importType,
		setImportTypeAndStep,
		resetProjectData,
	]);

	const onNext = useCallback(
		(nextImportType?: ImportType, supportedFlow?: ImporterFlow) => {
			const currentStep = productSteps[currentStepIndex].step;
			const shouldEnterFullScreen = fg('enable-x2c-standardised-flow')
				? currentStep === SELECT_SOURCE_STEP
				: currentStep === UPLOAD_CSV_STEP || currentStep === UPLOAD_CSV_STANDARD_STEP;
			if (shouldEnterFullScreen && nextImportType) {
				onEnterFullScreenMode();
				const nextStepAfterUploadCSV = getFirstStep({
					importType: nextImportType,
					projectType,
					source,
					supportedFlow,
				});
				// clear projectData if the user selects 3P importType
				if (nextImportType !== CSV) {
					resetProjectData();
				}
				if (nextImportType === CLICK_UP) {
					setThirdPartyImportSettings({
						shouldImportComments: false,
						shouldImportAttachments: false,
					});
				}
				setImportTypeAndStep({
					importType: nextImportType,
					step: nextStepAfterUploadCSV,
				});
				return;
			}
			const nextStep =
				currentStepConfig.overrides.overrideNextStep || productSteps[currentStepIndex + 1].step;
			setStep(nextStep);
		},
		[
			productSteps,
			currentStepIndex,
			currentStepConfig.overrides.overrideNextStep,
			setStep,
			onEnterFullScreenMode,
			projectType,
			source,
			setImportTypeAndStep,
			resetProjectData,
			setThirdPartyImportSettings,
		],
	);

	const baseStepsProps = { onNext, onReturn, onCloseAndReturnToUploadStep };

	const getContent = () => {
		switch (currentStepConfig.step) {
			case UPLOAD_CSV_STEP:
				return <UploadScreen {...baseStepsProps} />;
			case UPLOAD_CSV_STANDARD_STEP:
				return (
					<UploadScreenStandard
						{...currentStepConfig?.stepProps}
						{...baseStepsProps}
						onClose={() => onClose && onClose()}
					/>
				);
			case MAPPING_STEP:
				return <MappingScreen {...baseStepsProps} {...currentStepConfig.stepProps} />;
			case PROJECT_CREATE_STEP:
				return <ProjectCreateScreen {...baseStepsProps} {...currentStepConfig.stepProps} />;
			case IMPORT_IN_PROGRESS_STEP:
				return (
					<ImportInProgressScreen
						{...baseStepsProps}
						{...currentStepConfig.stepProps}
						onDrawerClose={onDrawerClose}
					/>
				);
			case SELECT_THIRD_PARTY_ENTITY_STEP:
				return (
					<SelectThirdPartyEntityScreen {...baseStepsProps} {...currentStepConfig.stepProps} />
				);
			case USER_MAPPING_STEP:
				return <UserMappingScreen {...baseStepsProps} {...currentStepConfig.stepProps} />;
			case CSV_USER_MAPPING_STEP:
				return <CSVUserMappingScreen {...baseStepsProps} {...currentStepConfig.stepProps} />;
			case THIRD_PARTY_REVIEW_STEP:
				return <ThirdPartyReviewScreen {...baseStepsProps} {...currentStepConfig.stepProps} />;
			case REVIEW_SCREEN_STANDARD_STEP:
				return <ReviewScreenStandard {...baseStepsProps} {...currentStepConfig.stepProps} />;
			default:
				return fg('enable-x2c-standardised-flow') ? (
					<SelectSourceScreen {...baseStepsProps} />
				) : (
					<SelectProductScreen />
				);
		}
	};

	const analyticsAttributes = useMemo(
		() => ({ projectType, projectStyle, importType, userAccess, source }),
		[userAccess, importType, projectStyle, projectType, source],
	);

	return (
		<AnalyticsAttributesContext.Provider value={analyticsAttributes}>
			<ContextualAnalyticsData attributes={analyticsAttributes}>
				<Wrapper>{getContent()}</Wrapper>
				<CancelImportModal />
				<RequestAccessTokenModal
					onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep}
					showCurrentAccount={
						'shouldShowAccountInfoIfUsingPAT' in currentStepConfig.stepProps &&
						currentStepConfig.stepProps.shouldShowAccountInfoIfUsingPAT === true
					}
				/>
			</ContextualAnalyticsData>
		</AnalyticsAttributesContext.Provider>
	);
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (props: Props) => {
	const importerContext = useImporterContextFromQueryParams();

	useEffect(() => {
		if (importerContext && importerContext?.importType !== DEFAULT) {
			props.onEnterFullScreenMode();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [importerContext?.importType]);

	return (
		<ImportStateContainer
			projectType={props.projectType}
			importerContext={importerContext}
			project={props.project}
		>
			<ProjectCreateImporterUploadFlow {...props} />
		</ImportStateContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	width: '100%',
	height: '100%',
});
